
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import singleDemo from './demo/single.twig';
const singleDemoProps = (args = {}, storyId) => {
  const props = {
    heading_id: args.heading_id || `${storyId}-heading`,
    href: args.href,
  };
  const classNames = [];
  const modifiers = [];
  if (args.horizontal !== 'none') {
    modifiers.push(`horizontal${args.horizontal}`);
  }
  if (args.contained) {
    modifiers.push('contained');
  }
  if (modifiers.length > 0) {
    classNames.push(...modifiers.map((modifier) => `c-card--${modifier}`));
  }
  if (args.theme !== 'none') {
    classNames.push(`t-${args.theme}`);
  }
  if (args.class) {
    classNames.push(args.class);
  }
  if (classNames.length > 0) {
    props.class = classNames.join(' ');
  }
  return props;
};
const singleDemoStory = (args) => {
  const props = singleDemoProps(args);
  if (args.show && args.show.length > 0) {
    for (const block of args.show) {
      props[`show_${block}`] = true;
    }
  }
  return singleDemo(props);
};
const singleDemoBlockExamples = {
  heading: 'Lorem ipsum dolor sit amet',
  cover: `<img src="https://placeimg.com/800/450/animals" alt="">`,
  content: `<p>Consectetur adipiscing elit...</p>`,
  footer: `<p>{{'now'|date('M j, Y')}}</p>`,
};
// Custom function for generating story source from args given
const singleDemoTransformSource = (_src, storyContext) => {
  const args = storyContext.args || storyContext.initialArgs;
  const props = singleDemoProps(args, storyContext.id);
  const propsString =
    Object.keys(props).length > 0
      ? ` with ${JSON.stringify(props, null, 2)}`
      : '';
  const blocks = (args.show || []).map(
    (blockName) =>
      `{% block ${blockName} %}${singleDemoBlockExamples[blockName]}{% endblock %}`
  );
  return `{% embed '@cloudfour/components/card/card.twig'${propsString} only %}
  ${blocks.join('\n  ')}
{% endembed %}`;
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Card" parameters={{
      docs: {
        transformSource: singleDemoTransformSource
      }
    }} args={{
      show: ['heading', 'cover', 'content', 'footer'],
      horizontal: 'none',
      theme: 'none'
    }} argTypes={{
      show: {
        options: ['heading', 'cover', 'content', 'footer'],
        control: {
          type: 'inline-check'
        }
      },
      heading_id: {
        type: {
          name: 'string'
        }
      },
      href: {
        type: {
          name: 'string'
        }
      },
      horizontal: {
        options: ['none', '@m', '@l', '@xl'],
        type: {
          name: 'string'
        },
        control: {
          type: 'inline-radio'
        }
      },
      contained: {
        type: {
          name: 'boolean'
        }
      },
      theme: {
        options: ['none', 'light', 'dark'],
        type: {
          name: 'string'
        },
        control: {
          type: 'inline-radio'
        }
      }
    }} mdxType="Meta" />
    <h1>{`Card`}</h1>
    <p>{`A card contains content and optionally an action summarizing a single piece of content, for example an article, talk or case study.`}</p>
    <p>{`If your card represents an `}<inlineCode parentName="p">{`article`}</inlineCode>{` and includes a heading, it's a good idea to provide a `}<inlineCode parentName="p">{`heading_id`}</inlineCode>{` as well. This allows us to use `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{` to improve the experience of navigating to individual cards via the `}<a parentName="p" {...{
        "href": "https://support.apple.com/guide/voiceover-guide/navigate-using-the-rotor-mchlp2719/web"
      }}>{`macOS VoiceOver rotor`}</a>{` (and potentially other assistive interfaces as well).`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Content Blocks" args={{
        show: ['heading', 'content', 'footer']
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`With link`}</h2>
    <p>{`If an `}<inlineCode parentName="p">{`href`}</inlineCode>{` property is provided, the `}<inlineCode parentName="p">{`heading`}</inlineCode>{` (if present) will include a link that encompasses the entire card.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Link" args={{
        href: '#',
        show: ['heading', 'content', 'footer']
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`With cover`}</h2>
    <p>{`An optional `}<inlineCode parentName="p">{`cover`}</inlineCode>{` block may be provided containing an image or other visual object. This will display using our `}<inlineCode parentName="p">{`wide`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "/story/design-tokens-aspect-ratio--page"
      }}>{`aspect ratio token`}</a>{`, and includes an effect on hover if the card includes an `}<inlineCode parentName="p">{`href`}</inlineCode>{` (see above).`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Cover Image" args={{
        href: '#'
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Horizontal`}</h2>
    <p>{`If a card with a cover is meant to occupy its full container width, it may be preferable to display it horizontally at larger `}<a parentName="p" {...{
        "href": "/docs/design-tokens-breakpoint--page"
      }}>{`breakpoints`}</a>{`. The `}<inlineCode parentName="p">{`c-card--horizontal@m`}</inlineCode>{`, `}<inlineCode parentName="p">{`c-card--horizontal@l`}</inlineCode>{` and `}<inlineCode parentName="p">{`c-card--horizontal@xl`}</inlineCode>{` modifier classes will do that from their respective breakpoints.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Horizontal" args={{
        href: '#',
        horizontal: '@m'
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <p>{`Horizontal cards will attempt to span all available columns of a CSS Grid Layout. This comes in handy when displaying them `}<a parentName="p" {...{
        "href": "/docs/objects-deck--horizontal-card#with-horizontal-cards"
      }}>{`in a Deck`}</a>{`.`}</p>
    <h2>{`With circular cover`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-card--circle-cover`}</inlineCode>{` modifier will change the appearance of the cover and adjacent content in the following ways:`}</p>
    <ul>
      <li parentName="ul">{`The cover image's aspect ratio will be square.`}</li>
      <li parentName="ul">{`The cover image will be fully rounded into a circle shape.`}</li>
      <li parentName="ul">{`When vertical, the card's text alignment will be centered to better align with the circle.`}</li>
      <li parentName="ul">{`When horizontal, the card's content will occupy more room than its image to account for the taller aspect ratio of the cover image.`}</li>
    </ul>
    <p>{`This variation is best suited for links to bio information, such as on our team page.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Circular Cover Image" args={{
        class: 'c-card--circle-cover',
        href: '#',
        horizontal: '@m'
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Contained`}</h2>
    <p>{`A card with the `}<inlineCode parentName="p">{`c-card--contained`}</inlineCode>{` class will gain a background color, padding and rounded corners. This can be helpful for offsetting the card visually from its surroundings.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Contained" args={{
        href: '#',
        horizontal: '@m',
        contained: true
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <p>{`When the card is a focal point and more contrast is desired, you may also attach a `}<a parentName="p" {...{
        "href": "/docs/design-themes--light"
      }}>{`theme class`}</a>{` to the card. In this example, the card has a class of `}<inlineCode parentName="p">{`t-light`}</inlineCode>{` within a `}<inlineCode parentName="p">{`t-dark`}</inlineCode>{` container:`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Themed" args={{
        href: '#',
        horizontal: '@m',
        contained: true,
        theme: 'light'
      }} parameters={{
        theme: 't-dark'
      }} mdxType="Story">
    {singleDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Template Properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`class`}</inlineCode>{` (string): Append a class to the root element.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`heading_id`}</inlineCode>{` (string): Adds this `}<inlineCode parentName="li">{`id`}</inlineCode>{` to the heading element (if a heading element is present); this also adds the `}<inlineCode parentName="li">{`aria-labelledby`}</inlineCode>{` property of the card element (unless `}<inlineCode parentName="li">{`aria_labelledby`}</inlineCode>{` is already specified).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`heading_level`}</inlineCode>{` (number, default `}<inlineCode parentName="li">{`2`}</inlineCode>{`): The header heading level (e.g. `}<inlineCode parentName="li">{`2`}</inlineCode>{` renders an `}<inlineCode parentName="li">{`<h2>`}</inlineCode>{` element).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`header_tag_name`}</inlineCode>{` (string, default `}<inlineCode parentName="li">{`'header'`}</inlineCode>{`): The header element tag.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`href`}</inlineCode>{` (string): When provided, the `}<inlineCode parentName="li">{`heading`}</inlineCode>{` (if present) will include a link that encompasses the entire card.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`footer_tag_name`}</inlineCode>{` (string, default `}<inlineCode parentName="li">{`'footer'`}</inlineCode>{`): The footer element tag.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`tag_name`}</inlineCode>{` (string, default `}<inlineCode parentName="li">{`'article'`}</inlineCode>{`): The root element tag.`}</li>
    </ul>
    <h2>{`Template Blocks`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`content`}</inlineCode>{` (optional): The main card content.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`cover`}</inlineCode>{` (optional): Designed to provide an image or other visual object. Will display using our `}<inlineCode parentName="li">{`wide`}</inlineCode>{` `}<a parentName="li" {...{
          "href": "/story/design-tokens-aspect-ratio--page"
        }}>{`aspect ratio token`}</a>{`, and includes an effect on hover if the card includes an `}<inlineCode parentName="li">{`href`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`footer`}</inlineCode>{` (optional): Information about the author or additional supporting content for the card.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`heading`}</inlineCode>{` (optional): The contents of the heading. The heading level may be customized using the `}<inlineCode parentName="li">{`heading_level`}</inlineCode>{` property.`}</li>
    </ul>
    <h2>{`Coming soon`}</h2>
    <ul>
      <li parentName="ul">{`Distinctly contained footers for PWA Stats (if necessary)`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const contentBlocks = singleDemoStory.bind({});
contentBlocks.storyName = 'Content Blocks';
contentBlocks.args = {
  show: ['heading', 'content', 'footer']
};
contentBlocks.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const link = singleDemoStory.bind({});
link.storyName = 'Link';
link.args = {
  href: '#',
  show: ['heading', 'content', 'footer']
};
link.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const coverImage = singleDemoStory.bind({});
coverImage.storyName = 'Cover Image';
coverImage.args = {
  href: '#'
};
coverImage.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const horizontal = singleDemoStory.bind({});
horizontal.storyName = 'Horizontal';
horizontal.args = {
  href: '#',
  horizontal: '@m'
};
horizontal.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const circularCoverImage = singleDemoStory.bind({});
circularCoverImage.storyName = 'Circular Cover Image';
circularCoverImage.args = {
  class: 'c-card--circle-cover',
  href: '#',
  horizontal: '@m'
};
circularCoverImage.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const contained = singleDemoStory.bind({});
contained.storyName = 'Contained';
contained.args = {
  href: '#',
  horizontal: '@m',
  contained: true
};
contained.parameters = { storySource: { source: 'singleDemoStory.bind({})' } };

export const themed = singleDemoStory.bind({});
themed.storyName = 'Themed';
themed.args = {
  href: '#',
  horizontal: '@m',
  contained: true,
  theme: 'light'
};
themed.parameters = { storySource: { source: 'singleDemoStory.bind({})' }, ...{
  theme: 't-dark'
} };

const componentMeta = { title: 'Components/Card', parameters: {
  docs: {
    transformSource: singleDemoTransformSource
  }
}, args: {
  show: ['heading', 'cover', 'content', 'footer'],
  horizontal: 'none',
  theme: 'none'
}, argTypes: {
  show: {
    options: ['heading', 'cover', 'content', 'footer'],
    control: {
      type: 'inline-check'
    }
  },
  heading_id: {
    type: {
      name: 'string'
    }
  },
  href: {
    type: {
      name: 'string'
    }
  },
  horizontal: {
    options: ['none', '@m', '@l', '@xl'],
    type: {
      name: 'string'
    },
    control: {
      type: 'inline-radio'
    }
  },
  contained: {
    type: {
      name: 'boolean'
    }
  },
  theme: {
    options: ['none', 'light', 'dark'],
    type: {
      name: 'string'
    },
    control: {
      type: 'inline-radio'
    }
  }
}, includeStories: ["contentBlocks","link","coverImage","horizontal","circularCoverImage","contained","themed"],  };

const mdxStoryNameToKey = {"Content Blocks":"contentBlocks","Link":"link","Cover Image":"coverImage","Horizontal":"horizontal","Circular Cover Image":"circularCoverImage","Contained":"contained","Themed":"themed"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;